import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { Button, TextField, Typography } from '@mui/material';
import logo from '../login/assets/robodebt-logo.svg';
import '../login/login.scss';
import apiClient from '../../shared/auth.interceptor';
import { doLogin } from '../../shared/auth.functions';
import { apiMessage, errorToast } from '../../shared/toast.service';
import { getPasswordErrorMessages, passwordValidator } from '../../validate';
import SignUpPassInput from '../Signup/passfield';
import { MainRoute } from '../../../app';
import { UserRoute } from '../../user/userContainer';
import { initializeApp } from '../../../app.init';

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Where the invite email sends a new user: they get a code by email, choose a
 * password and are signed in. The email comes from the invite link.
 */
const AcceptInvite: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [params] = useSearchParams();
  const invitedEmail = (params.get('email') || '').trim();
  const [email, setEmail] = useState(invitedEmail);
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [touched, setTouched] = useState({ email: false, code: false, password: false, confirmPassword: false });
  const [busy, setBusy] = useState(false);

  const passwordErrors = passwordValidator(password);
  const passwordMessage = getPasswordErrorMessages(passwordErrors);
  const mismatch = Boolean(confirmPassword) && password !== confirmPassword;
  const validEmail = EMAIL.test(email.trim());

  const sendCode = async () => {
    setBusy(true);
    try {
      await apiClient.get(`/v1/auth/code?email=${encodeURIComponent(email.trim())}`);
      setCodeSent(true);
      toast.success(`We emailed a code to ${email.trim()}`);
    } catch (error) {
      errorToast(apiMessage(error, 'The code could not be sent'));
    } finally {
      setBusy(false);
    }
  };

  const setUp = async () => {
    setBusy(true);
    try {
      await apiClient.post('/v1/auth/sign-up', { email: email.trim(), code: code.trim(), password });
    } catch (error) {
      errorToast(apiMessage(error, 'Your account could not be set up'));
      setBusy(false);
      return;
    }
    try {
      const response = await apiClient.post('/v1/auth/login', { email: email.trim(), password });
      doLogin(response.data);
      await dispatch(initializeApp() as any);
      toast.success('Welcome to RoboDebit!');
      navigate(`${MainRoute.User}${UserRoute.Clients}`);
    } catch {
      toast.success('Your account is ready. Log in with your email and password.');
      navigate(MainRoute.SignIn);
    }
  };

  const touch = (name: keyof typeof touched) => setTouched((prev) => ({ ...prev, [name]: true }));
  const canSetUp = validEmail && code.trim().length === 6 && !passwordMessage && password === confirmPassword && !busy;

  return (
    <div className="container-auth-page">
      <div className="main-auth-form">
        <img alt="logo" src={logo} className="logo" />
        <form className="input-form px-3" onSubmit={(e) => e.preventDefault()}>
          <h3 className="fw-bold">Set Up Your Account</h3>
          <Typography variant="subtitle2" gutterBottom>
            {codeSent
              ? 'Enter the code from your email and choose a password.'
              : "You've been invited to RoboDebit. We'll email you a code so you can choose your password."}
          </Typography>
          <TextField
            fullWidth
            type="email"
            name="email"
            label="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => touch('email')}
            error={touched.email && !validEmail}
            helperText={touched.email && !validEmail ? 'Please enter a valid email address' : ''}
            disabled={codeSent || Boolean(invitedEmail)}
            variant="outlined"
            margin="normal"
          />
          {!codeSent ? (
            <Button
              disabled={!validEmail || busy}
              variant="contained"
              className="login_button w-100 my-2"
              onClick={sendCode}
            >
              Email Me a Code
            </Button>
          ) : (
            <>
              <TextField
                fullWidth
                name="code"
                label="Verification Code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                onBlur={() => touch('code')}
                error={touched.code && code.trim().length !== 6}
                helperText={touched.code && code.trim().length !== 6 ? 'The code has 6 digits' : ''}
                inputProps={{ inputMode: 'numeric', autoComplete: 'one-time-code' }}
                variant="outlined"
                margin="normal"
              />
              <SignUpPassInput
                errors={passwordErrors}
                touched={touched}
                passwordVisible={passwordVisible}
                setPasswordVisible={setPasswordVisible}
                password={password}
                handleInputChange={(e) => setPassword(e.target.value)}
                handleInputBlur={() => touch('password')}
                label="Password"
                name="password"
                helperText={touched.password ? passwordMessage : ''}
                error={Boolean(passwordMessage && touched.password)}
              />
              <SignUpPassInput
                errors={{}}
                touched={touched}
                passwordVisible={passwordVisible}
                setPasswordVisible={setPasswordVisible}
                password={confirmPassword}
                handleInputChange={(e) => setConfirmPassword(e.target.value)}
                handleInputBlur={() => touch('confirmPassword')}
                label="Confirm Password"
                name="confirmPassword"
                helperText={touched.confirmPassword && mismatch ? 'Passwords do not match' : ''}
                error={touched.confirmPassword && mismatch}
              />
              <Button disabled={!canSetUp} variant="contained" className="login_button w-100 my-2" onClick={setUp}>
                Set Up My Account
              </Button>
              <div className="need-help-div justify-content-center">
                <Link to="#" onClick={(e) => { e.preventDefault(); if (!busy) void sendCode(); }}>
                  Send a new code
                </Link>
              </div>
            </>
          )}
          <div className="need-help-div justify-content-center">
            <Link to={MainRoute.SignIn}>Already set your password? Log in</Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AcceptInvite;
